import type * as React from 'react';
import {useEffect, useMemo, useState} from 'react';
import type {FilterSearch} from './CapacityAppBar';
import CapacityAppBar from './CapacityAppBar';
import CapacityList from './CapacityList';
import {favoritesTeamId, unscheduledTeamId} from '@/components/SideBar/PersistentDrawerLeft';
import {usePreferencesState} from '@/context/PreferencesProvider';
import getCurrentCapacityYearMonth from '@/functions/getCurrentCapacityYearMonth';
import {useGetCapacityDateConfig} from '@/hooks/capacity/useGetCapacityDateConfig';
import {useGetSelf} from '@/hooks/useGetSelf';
import type {ClientServiceTeam} from '@/types/ClientServiceTeam';
import type {Employee} from '@/types/Employee';

type Props = {
    employees : Employee[] | null;
    filtersActive : boolean;
    teams : ClientServiceTeam[];
};

const Capacity : React.FC<Props> = (
    {
        employees,
        filtersActive,
        teams,
    } : Props
) => {
    const {preferences} = usePreferencesState();
    const [filterSearch, setFilterSearch] = useState<FilterSearch>({query: '', type: 'people'});
    const getCapacityDateConfigQuery = useGetCapacityDateConfig();
    const useGetSelfQuery = useGetSelf();
    const [fiscalMonth, setFiscalMonth] = useState<string | null>(null);
    const [defaultFiscalMonth, setDefaultFiscalMonth] = useState<string | null>(null);

    const filteredEmployees = useMemo(() => {
        if (!employees) {
            return null;
        }

        let filteredEmployees = employees;
        const {favoriteEmployees, selectedPracticeAreas} = preferences.capacity;
        const selectedTeams = new Set(preferences.capacity.selectedTeams);

        if (filtersActive) {
            const showUnassignedSubcontractors = selectedTeams.has(unscheduledTeamId);
            selectedTeams.delete(unscheduledTeamId);

            // Collect employee IDs from selected teams first
            // Exclude special filter IDs (favorites, unscheduled) when looking up actual teams
            let employeeIdsInSelectedTeams : number[] = [];

            if (selectedTeams.size > 0) {
                const actualTeamIds = [...selectedTeams].filter(id => id !== favoritesTeamId);
                const selectedClientServiceTeams = actualTeamIds
                    .map(teamId => teams.find(team => team.id === teamId))
                    .filter((team) : team is ClientServiceTeam => team !== undefined);

                for (const team of selectedClientServiceTeams) {
                    employeeIdsInSelectedTeams = employeeIdsInSelectedTeams
                        .concat(team.associates.map(({associateId}) => associateId));
                }

                employeeIdsInSelectedTeams = [...new Set(employeeIdsInSelectedTeams)];
            }

            // Filter out unscheduled subcontractors (those NOT in selected teams)
            if (!showUnassignedSubcontractors) {
                filteredEmployees = filteredEmployees.filter(employee => {
                    // Keep all employees (non-subcontractors)
                    if (employee.type !== 'subcontractor') {
                        return true;
                    }

                    // Keep subcontractors who ARE in selected teams (scheduled)
                    // If no teams selected, keep all subcontractors
                    if (employeeIdsInSelectedTeams.length === 0) {
                        return true;
                    }

                    return employeeIdsInSelectedTeams.includes(employee.id);
                });
            }

            // Filter by selected teams
            if (selectedTeams.size > 0) {
                filteredEmployees = filteredEmployees.filter(
                    employee => {
                        if (selectedTeams.has(favoritesTeamId) && favoriteEmployees.includes(employee.id)) {
                            return true;
                        }

                        return employeeIdsInSelectedTeams.includes(employee.id);
                    }
                );
            }

            if (selectedPracticeAreas.length > 0) {
                filteredEmployees = filteredEmployees.filter(employee => {
                    const matchedPracticeAreas = selectedPracticeAreas.filter(
                        practiceArea => employee.practiceAreas.includes(practiceArea)
                    );

                    return matchedPracticeAreas.length === selectedPracticeAreas.length;
                });
            }
        }

        if (filterSearch.query !== '') {
            const normalizedQuery = filterSearch.query.toLocaleLowerCase();

            if (filterSearch.type === 'people') {
                filteredEmployees = filteredEmployees.filter(employee => {
                    const name = `${employee.firstName} ${employee.lastName}`.toLocaleLowerCase();
                    return name.includes(normalizedQuery);
                });
            }
        }

        return filteredEmployees;
    }, [employees, preferences.capacity, filtersActive, filterSearch.query, filterSearch.type, teams]);

    useEffect(() => {
        if (getCapacityDateConfigQuery.data) {
            const currentFiscalMonth = getCurrentCapacityYearMonth(getCapacityDateConfigQuery.data);

            if (currentFiscalMonth) {
                setDefaultFiscalMonth(currentFiscalMonth);
                setFiscalMonth(currentFiscalMonth);
            }
        }
    }, [getCapacityDateConfigQuery.data]);

    return (
        <>
            {fiscalMonth && defaultFiscalMonth && getCapacityDateConfigQuery.data && <CapacityAppBar
                fiscalMonth={fiscalMonth}
                setFiscalMonth={setFiscalMonth}
                filterSearch={filterSearch}
                capacityConfig={getCapacityDateConfigQuery.data}
                defaultCapacityYearMonth={defaultFiscalMonth}
                onFilterSearchChange={setFilterSearch}
            />}

            {!fiscalMonth
                || !filteredEmployees
                || !getCapacityDateConfigQuery.data
                || !useGetSelfQuery.data
                || defaultFiscalMonth !== null
             && <CapacityList
                 employees={filteredEmployees}
                 teams={teams}
                 fiscalMonth={fiscalMonth}
                 defaultCapacityYearMonth={defaultFiscalMonth}
                 capacityConfig={getCapacityDateConfigQuery.data}
             />}
        </>
    );
};

export default Capacity;
